import { Box, Tooltip, Typography } from "@mui/material";
import { useTheme } from '@mui/material/styles';
import moment from "moment-timezone";
import { ReactComponent as InfoIcon } from '../../../assets/info-icon-dim.svg';

interface TimelineDot {
    projectName: string;
    slaTime: string;
    actualCompletionTime?: string | null;
    slaStatus: string;
    colorCode: string;
    dataAvailability?: string | number;
}

interface CustomLinearProgressProps {
    timeline: TimelineDot[];
    isPBNA?: boolean;
}

// Responsive inset: 7vw or max 50px
const DOT_INSET: any = 'clamp(16px, 7vw, 50px)';
const DOT_SIZE = 'clamp(14px, 2vw, 24px)';

const CustomLinearProgress: React.FC<CustomLinearProgressProps> = ({
    timeline,
    isPBNA = false
}) => {
    const theme = useTheme();
    const colors = theme.palette.dataPipeline.accordionWidget;

    if (!timeline.length) return null;

    // If all statuses are Not Started or In Progress, show only first dot direct value
    const dotCount = timeline.length;

    const renderSlaTime = (dot: any, isPBNA: boolean) => {
        if (!isPBNA && dot.projectName === 'MDT') return ' - ';

        return `SLA Time: ${moment.utc(dot.slaTime).format('hh:mm A')} CST`;
    };

    const renderActualCompletion = (dot: any) => {
        if (dot.actualCompletionTime) {
            return `Actual Completion Time: ${moment(dot.actualCompletionTime)
                .utc()
                .format('hh:mm A')} CST`;
        }

        return null;
    };

    const renderCompletionInfo = (dot: any) => {
        if (dot.actualCompletionTime) {
            return (
                <Typography
                    fontSize={{ xs: 12, sm: 13, md: 14 }}
                    color={colors.text.secondary}
                    fontStyle="italic"
                    fontWeight={400}
                >
                    {renderActualCompletion(dot)}
                </Typography>
            );
        }

        if (dot.slaStatus === 'SLA Missed') {
            return (
                <Typography
                    fontSize={{ xs: 12, sm: 13, md: 14 }}
                    color={colors.text.secondary}
                    fontStyle="italic"
                    fontWeight={400}
                >
                    Actual Completion Time: Not Available{' '}
                    <Tooltip
                        title={
                            <Typography
                                variant="body2"
                                sx={{
                                    fontSize: { xs: 11, sm: 12, md: 13 },
                                    color: colors.text.tooltipPrimary,
                                    fontWeight: 500
                                }}
                            >
                                Click on the hop for more details
                            </Typography>
                        }
                        arrow
                        slotProps={{
                            tooltip: {
                                sx: {
                                    backgroundColor: `${colors.timelineTooltip?.background} !important`,
                                    color: `${colors.timelineTooltip?.text} !important`,
                                    borderRadius: '16px',
                                    boxShadow: colors.timelineTooltip?.shadow,
                                    px: 2,
                                    py: 1.5
                                }
                            },
                            arrow: {
                                sx: {
                                    color: `${colors.timelineTooltip?.background} !important`,
                                    '&:before': {
                                        boxShadow: colors.timelineTooltip?.shadow
                                    }
                                }
                            }
                        }}
                    >
                        <InfoIcon style={{ verticalAlign: 'text-bottom' }} />
                    </Tooltip>
                </Typography>
            );
        }

        return null;
    };

    const renderDataAvailability = (dot: any) => {
        if (
            dot.dataAvailability === undefined ||
            dot.dataAvailability === null ||
            dot.dataAvailability === ''
        ) {
            return null;
        }

        return (
            <Typography
                fontSize={{ xs: 10, sm: 12, md: 14 }}
                fontStyle="italic"
                color={colors.text.primary}
                fontWeight={400}
                sx={{ mt: 0.5 }}
            >
                Data Availability: {dot.dataAvailability}
                {typeof dot.dataAvailability === 'number' ? '%' : ''}
            </Typography>
        );
    };

    const renderProjectBadge = (dot: any) => (
        <Box
            sx={{
                mt: 1.2,
                bgcolor: `${dot.colorCode || colors.background.statusMuted} !important`,
                color: colors.text.inverse,
                fontSize: { xs: 11, sm: 13, md: 16 },
                fontWeight: 700,
                px: { xs: 1.5, sm: 2.5, md: 3 },
                py: 0.7,
                borderRadius: '999px',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: { xs: 60, sm: 90, md: 140, lg: 190 },
                lineHeight: 1,
                marginBottom: { xs: '6px', sm: '10px' },
                textTransform: 'uppercase',
                textAlign: 'center',
                wordBreak: 'break-word'
            }}
        >
            {dot.projectName}
        </Box>
    );

    return (
        <Box
            sx={{
                position: "relative",
                width: "100%",
                minWidth: 220,
                height: { xs: 48, sm: 60, md: 70 },
                px: { xs: 1, sm: 2, md: 3 },
                boxSizing: "border-box",
                overflow: 'visible'
            }}
        >
            {/* Horizontal line */}
            <Box
                sx={{
                    position: "absolute",
                    top: "20%",
                    left: 0,
                    right: 0,
                    height: 'clamp(6px, 1vw, 12px)',
                    backgroundColor: colors.background.timelineBar,
                    borderRadius: "4px",
                    transform: "translateY(-50%)",
                    zIndex: 1
                }}
            />

            {/* Dots */}
            {timeline.map((dot, idx) => {
                let left;

                if (dotCount === 1) {
                    left = '50%';
                } else if (dotCount > 1) {
                    const percent = idx / (dotCount - 1);

                    left = `calc(${DOT_INSET} + ${percent} * (100% - 2 * ${DOT_INSET}))`;
                }

                let labelLeft = "50%";
                let labelTransform = "translateX(-50%)";
                let labelWidth: any = undefined;
                let labelTextAlign: any = "center";

                if (
                    dotCount !== 1 &&
                    idx === 0 &&
                    dot.projectName.length > 10
                ) {
                    labelLeft = `calc(50% - ${DOT_INSET})`;
                    labelTransform = "translateX(0)";
                    labelWidth = `calc(100% + ${DOT_INSET})`;
                    labelTextAlign = "left";
                }

                if (
                    dotCount !== 1 &&
                    idx === dotCount - 1
                ) {
                    if (dot.projectName.length > 10) {
                        labelLeft = `calc(50% + ${DOT_INSET})`;
                        labelTransform = "translateX(-100%)";
                        labelWidth = `calc(100% + ${DOT_INSET})`;
                        labelTextAlign = "right";
                    } else {
                        labelLeft = "50%";
                        labelTransform = "translateX(-50%)";
                        labelWidth = "auto";
                        labelTextAlign = "center";
                    }
                }

                return (
                    <Box
                        key={`${dot.projectName}-${idx}`}
                        sx={{
                            position: "absolute",
                            top: "20%",
                            left,
                            zIndex: 2,
                            transform: "translate(-50%, -50%)",
                            width: dotCount > 1
                                ? `calc((100% - 2 * ${DOT_INSET}) / ${dotCount - 1})`
                                : 'auto',
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center"
                        }}
                    >
                        {/* Hop name */}
                        <Box
                            sx={{
                                position: "absolute",
                                top: idx % 2 === 0 ? "-28px" : "35px",
                                left: labelLeft,
                                transform: labelTransform,
                                maxWidth: dotCount > 1 ? "100%" : "none",
                                width: labelWidth,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                textAlign: labelTextAlign,
                                color: colors.text.primary,
                                fontWeight: 500,
                                fontSize: { xs: 10, sm: 12, md: 14 }
                            }}
                        >
                            {dot.projectName}
                        </Box>

                        {/* Dot with tooltip */}
                        <Tooltip
                            placement="top"
                            arrow
                            title={
                                <Box
                                    sx={{
                                        textAlign: "center",
                                        minWidth: { xs: 160, sm: 200, md: 220 }
                                    }}
                                >
                                    <Typography
                                        fontSize={{ xs: 11, sm: 13, md: 16 }}
                                        fontWeight={500}
                                        color={colors.text.primary}
                                    >
                                        {renderSlaTime(dot, isPBNA)}
                                    </Typography>

                                    {renderCompletionInfo(dot)}

                                    {renderDataAvailability(dot)}

                                    {renderProjectBadge(dot)}
                                </Box>
                            }
                            slotProps={{
                                tooltip: {
                                    sx: {
                                        backgroundColor: `${colors.timelineTooltip?.background} !important`,
                                        color: `${colors.timelineTooltip?.text} !important`,
                                        borderRadius: '16px',
                                        boxShadow: colors.timelineTooltip?.shadow,
                                        px: { xs: 1.5, sm: 2, md: 2.5 },
                                        py: { xs: 1.25, sm: 1.5, md: 2 }
                                    }
                                },
                                arrow: {
                                    sx: {
                                        color: `${colors.timelineTooltip?.background} !important`,
                                        '&:before': {
                                            boxShadow: colors.timelineTooltip?.shadow
                                        }
                                    }
                                }
                            }}
                        >
                            <Box
                                sx={{
                                    width: DOT_SIZE,
                                    height: DOT_SIZE,
                                    borderRadius: "50%",
                                    bgcolor: `${dot.colorCode || colors.background.statusMuted}`,
                                    border: `2px solid ${colors.background.timelineDotBorder}`,
                                    boxShadow: `0 0 0 2px ${colors.background.timelineDotRing}`,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    cursor: "pointer",
                                    transition: "width 0.2s, height 0.2s"
                                }}
                            />
                        </Tooltip>
                    </Box>
                );
            })}
        </Box>
    );
};

export default CustomLinearProgress;